// Pure localStorage access, without the API client: `lib/api.ts` reads it to
// mark every call made during an impersonation (no import cycle).

/**
 * The only client-side trace of an admin working inside a client's account.
 *
 * During an impersonation the Supabase session is genuinely the target's:
 * RLS, realtime and agent tokens all behave as for the client. What makes it
 * an impersonation is this record: it shows the banner and carries the signed
 * audit token (`token`, minted by `POST /api/admin/impersonate`) that the
 * backend needs to hand the admin session back on exit. No admin session is
 * kept on this side: leaving mints a fresh magic link for the admin.
 */
export const IMPERSONATION_STORAGE_KEY = "oxpium:impersonation";

export type ImpersonationState = {
  /** Signed audit token (`aud: ox-impersonation`), sent back on exit. */
  token: string;
  /** The user whose session is active. */
  targetId: string;
  /** The admin who started it — the one we return to. */
  adminId: string;
  /** Epoch ms; mirrors the token's own expiry. */
  expiresAt: number;
};

export function readImpersonation(): ImpersonationState | null {
  try {
    const raw = window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ImpersonationState>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.targetId !== "string" ||
      typeof parsed.adminId !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      clearImpersonation();
      return null;
    }
    if (parsed.expiresAt <= Date.now()) {
      clearImpersonation();
      return null;
    }
    return parsed as ImpersonationState;
  } catch {
    return null;
  }
}

/**
 * The record only means something while the session it describes is the one
 * in use: after a plain sign-out and a sign-in as someone else, a leftover
 * record must not show a lying banner.
 */
export function activeImpersonation(
  currentUserId: string | null | undefined,
): ImpersonationState | null {
  if (!currentUserId) return null;
  const state = readImpersonation();
  return state?.targetId === currentUserId ? state : null;
}

export function clearImpersonation(): void {
  try {
    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * The header that marks an API call made during an impersonation: the
 * signed token, sent only while the record belongs to the session in use.
 * The backend keeps billing read-only on it (`routes/app/billing.ts`) — a
 * per-request rule, so the real user is never blocked by a session an admin
 * left open elsewhere.
 */
export function impersonationHeaders(
  currentUserId: string | null | undefined,
): Record<string, string> {
  const state = activeImpersonation(currentUserId);
  return state ? { "x-ox-impersonation": state.token } : {};
}
